import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Mail, MapPin, Phone } from "lucide-react";
import { Footer } from "@/components/Footer";

gsap.registerPlugin(ScrollTrigger);

const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Val-Hue%20High%20School%20and%20Junior%20College%20Manjari%20Budruk%20Pune%20412307";

const IconRing = ({ children }) => (
  <span className="relative grid h-14 w-14 shrink-0 place-items-center transition-transform duration-300 group-hover:-translate-y-1 group-active:-translate-y-1">
    <svg
      viewBox="0 0 56 56"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <circle
        cx="28"
        cy="28"
        r="26.5"
        fill="none"
        stroke="rgba(20,58,102,0.18)"
        strokeWidth="1.5"
      />
      <circle
        cx="28"
        cy="28"
        r="26.5"
        pathLength="1"
        fill="none"
        stroke="#F47221"
        strokeWidth="1.5"
        strokeLinecap="round"
        className="ring-draw"
        transform="rotate(-90 28 28)"
      />
    </svg>
    <span className="text-[#143A66] transition-colors duration-300 group-hover:text-[#F47221] group-active:text-[#F47221]">
      {children}
    </span>
  </span>
);

const Label = ({ children }) => (
  <span className="block text-[11px] font-semibold uppercase tracking-[0.3em] text-[#143A66]/50">
    {children}
  </span>
);

export const ContactSection = () => {
  const sectionRef = useRef(null);
  const ruleRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.from("[data-contact-row]", {
          y: 28,
          opacity: 0,
          duration: 0.7,
          stagger: 0.12,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            once: true,
          },
        });
        gsap.from(ruleRef.current, {
          scaleX: 0,
          transformOrigin: "left center",
          duration: 0.9,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            once: true,
          },
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="relative bg-[#F7FAFB]"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
        <p className="text-[11px] font-semibold uppercase tracking-[0.45em] text-[#F47221]">
          Get in touch
        </p>
        <h2
          className="mt-4 font-display text-4xl font-semibold text-[#143A66] md:text-5xl"
          data-testid="contact-heading"
        >
          Contact Us
        </h2>
        <span
          ref={ruleRef}
          className="mt-6 block h-[2px] w-24 bg-[#143A66]"
          aria-hidden="true"
        />

        <div className="mt-14 grid gap-12 md:mt-20 md:grid-cols-[1.05fr_1fr] md:gap-16">
          <div className="space-y-9">
            <a
              href="mailto:valhue96@gmail.com"
              data-testid="contact-email-link"
              data-contact-row
              className="group flex items-center gap-5"
            >
              <IconRing>
                <Mail className="h-5 w-5" strokeWidth={1.75} />
              </IconRing>
              <span>
                <Label>Email</Label>
                <span className="mt-1 block text-base font-medium text-[#143A66] md:text-lg">
                  valhue96@gmail.com
                </span>
              </span>
            </a>

            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-location-link"
              data-contact-row
              className="group flex items-center gap-5"
            >
              <IconRing>
                <MapPin className="h-5 w-5" strokeWidth={1.75} />
              </IconRing>
              <span>
                <Label>Visit</Label>
                <span className="mt-1 block text-base font-medium leading-snug text-[#143A66] md:text-lg">
                  Val Hue Education School, Manjari Budruk, Pune 412307
                </span>
              </span>
            </a>

            <div className="group flex items-center gap-5" data-contact-row>
              <IconRing>
                <Phone className="h-5 w-5" strokeWidth={1.75} />
              </IconRing>
              <span>
                <Label>Call</Label>
                <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-base font-medium md:text-lg">
                  <a
                    href="tel:9552528829"
                    data-testid="contact-phone-1-link"
                    className="text-[#143A66] underline-offset-4 transition-colors hover:text-[#F47221] hover:underline"
                  >
                    9552528829
                  </a>
                  <span className="text-[#143A66]/30" aria-hidden="true">
                    /
                  </span>
                  <a
                    href="tel:9545663939"
                    data-testid="contact-phone-2-link"
                    className="text-[#143A66] underline-offset-4 transition-colors hover:text-[#F47221] hover:underline"
                  >
                    9545663939
                  </a>
                </span>
              </span>
            </div>
          </div>

          <div data-contact-row>
            <div className="overflow-hidden rounded-xl border border-[#143A66]/10 shadow-sm">
              <iframe
                title="Map — Val-Hue High School, Manjari Budruk, Pune"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Val-Hue%20High%20School%20and%20Junior%20College%2C%20Manjari%20Budruk%2C%20Pune%20412307&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                className="h-64 w-full grayscale-[30%] md:h-72"
              />
            </div>
            <p className="mt-3 text-xs text-[#143A66]/60">
              Campus location — Manjari Budruk, Pune
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </section>
  );
};
